import React, { useState, useEffect } from 'react';
import { AuthProvider } from './auth/AuthContext';
import PublicSignup from './pages/PublicSignup';
import AuthGate from './shell/AuthGate';
import { ERPToastContainer } from './components/erp/ERPToast';
import { I18nProvider } from './i18n/I18nContext';
import { useDevToolsGuard } from './security/useDevToolsGuard';
import './security/SecurityStyles.css';

// ─── App Shell ──────────────────────────────────────────────────────────────────
export default function App() {
  // Block DevTools shortcuts + right-click in production builds
  useDevToolsGuard();
  // Apply theme: saved preference → OS preference → fallback dark
  useEffect(() => {
    const saved = localStorage.getItem('erp_theme');
    if (saved) {
      document.documentElement.setAttribute('data-theme', saved);
      return;
    }
    // No saved preference — detect OS preference with live listener
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
    document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => {
      if (!localStorage.getItem('erp_theme')) {
        document.documentElement.setAttribute('data-theme', e.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // ── Public, pre-login routes (no AuthProvider wrap needed) ───────────
  // Path-based router (avoids pulling react-router into the rest of the
  // app). Routes `/signup` and `/landing` render the tiered signup flow
  // without requiring login. Anything else falls through to AuthGate.
  const [publicPath, setPublicPath] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  useEffect(() => {
    const onNav = () => setPublicPath(window.location.pathname);
    window.addEventListener('popstate', onNav);
    return () => window.removeEventListener('popstate', onNav);
  }, []);
  const goToLogin = () => {
    window.history.pushState({}, '', '/');
    setPublicPath('/');
  };
  if (publicPath === '/signup' || publicPath === '/landing') {
    return (
      <I18nProvider>
        <PublicSignup
          initialStep={publicPath === '/landing' ? 'mode' : 'mode'}
          onExitToLogin={goToLogin}
        />
        <ERPToastContainer />
      </I18nProvider>
    );
  }

  return (
    <I18nProvider>
      <AuthProvider>
        <AuthGate />
        <ERPToastContainer />
      </AuthProvider>
    </I18nProvider>
  );
}
